// Brief + checklist for a campaign. Every video in the campaign is checked
// against this list; critical items decide FAIL.
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { DIMENSIONS, groupByDimension } from './qcFormat';
import { errorText } from './qcClient';
import { AI, AiMark, CriticalTag, DimensionIcon } from './aiUi';
import { Button, C, CardHeader, Disclosure, SURFACE } from './ui';

export const nextItemId = (items) => {
    const used = new Set(items.map((i) => i.id));
    let n = items.length + 1;
    while (used.has(`c${n}`)) n += 1;
    return `c${n}`;
};

export const cleanItems = (items) => items
    .map((i) => ({ ...i, requirement: i.requirement.trim() }))
    .filter((i) => i.requirement);

const BRIEF_ID = 'vqc-brief';

const inputStyle = { width: '100%', padding: '9px 11px', border: `1px solid ${C.border}`, borderRadius: 10, fontSize: 13, fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box' };

// One row per item; on a narrow card the requirement wraps under the dimension.
function ItemEditor({ item, onChange, onRemove }) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <select aria-label="Dimension" value={item.dimension} onChange={(e) => onChange({ ...item, dimension: e.target.value })} style={{ ...inputStyle, width: 150, flexShrink: 0 }}>
                {DIMENSIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <input aria-label="Requirement" value={item.requirement} placeholder="e.g. Ad disclosure (#ad) visible on screen"
                onChange={(e) => onChange({ ...item, requirement: e.target.value })} style={{ ...inputStyle, width: 'auto', flex: '1 1 240px', minWidth: 0 }} />
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 32, padding: '0 4px', fontSize: 12, color: C.body, whiteSpace: 'nowrap', cursor: 'pointer' }}>
                <input type="checkbox" checked={item.critical} onChange={(e) => onChange({ ...item, critical: e.target.checked })} style={{ margin: 0 }} /> Critical
            </label>
            <button type="button" aria-label="Remove item" className="vqc-focus vqc-icon-btn" onClick={onRemove}
                style={{ width: 32, height: 32, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', borderRadius: 8, color: C.muted, fontSize: 18, lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>
    );
}

function ChecklistView({ checklist }) {
    if (!checklist.length) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', borderRadius: 14, background: AI.wash, border: `1px dashed ${AI.washBorder}` }}>
                <AiMark size={40} />
                <p style={{ fontSize: 13, color: C.body, margin: 0, lineHeight: 1.5 }}>
                    <b style={{ color: C.text }}>No checklist yet.</b> Draft one from the brief with AI, or add items by hand.
                    The AI reviewer checks every video against this list.
                </p>
            </div>
        );
    }
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {groupByDimension(checklist).map((group) => (
                <div key={group.id} style={{ background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '12px 14px' }}>
                    <div title={group.hint} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 700, letterSpacing: 0.4, color: C.th, textTransform: 'uppercase', marginBottom: 4 }}>
                        <DimensionIcon id={group.id} size={16} color={C.navy700} />
                        {group.label}
                        <span style={{ marginLeft: 'auto', fontWeight: 600, color: C.muted, letterSpacing: 0 }}>{group.items.length}</span>
                    </div>
                    {group.items.map((item) => (
                        <div key={item.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 5, padding: '8px 0 2px', borderTop: `1px solid ${C.line}`, marginTop: 6, fontSize: 13, color: C.body, lineHeight: 1.4 }}>
                            <span>{item.requirement}</span>
                            {item.critical && <CriticalTag />}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

// While the AI drafts the checklist (the service takes 10-40 seconds).
const Drafting = () => (
    <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 12, background: AI.wash, border: `1px solid ${AI.washBorder}`, marginBottom: 14 }}>
        <AiMark size={34} active />
        <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Reading the brief and drafting requirements</div>
            <div style={{ fontSize: 12, color: C.muted, margin: '2px 0 8px' }}>This takes 10 to 40 seconds. Review the draft before you run QC.</div>
            <span aria-hidden="true" className="vqc-seg-active" style={{ display: 'block', height: 5, borderRadius: 5 }} />
        </div>
    </div>
);

export default function ChecklistPanel({ brief, checklist, client, onChange, campaignBrief = '', sample = false, track = () => {} }) {
    const [editing, setEditing] = useState(false);
    const [draftBrief, setDraftBrief] = useState(brief);
    const [draftItems, setDraftItems] = useState(checklist);
    const [busy, setBusy] = useState(null);
    const [confirmExtract, setConfirmExtract] = useState(false);
    const [showBrief, setShowBrief] = useState(false);

    // An empty QC brief starts from the campaign's own brief, if it has one.
    const startEdit = () => { setDraftBrief(brief || campaignBrief || ''); setDraftItems(checklist); setEditing(true); };
    const setItem = (index, next) => setDraftItems(draftItems.map((it, i) => (i === index ? next : it)));
    const addItem = () => setDraftItems([...draftItems, { id: nextItemId(draftItems), dimension: 'visual', requirement: '', critical: false }]);

    const save = async () => {
        setBusy('save');
        try {
            const saved = await client.saveBrief({ brief: draftBrief, checklist: cleanItems(draftItems) });
            onChange(saved);
            setEditing(false);
            toast.success(sample ? 'Saved for this session only (sample data)' : 'Brief and checklist saved');
            track('video_qc_checklist_saved', { items: saved.checklist.length });
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setBusy(null);
        }
    };

    const extract = async () => {
        setConfirmExtract(false);
        setBusy('extract');
        try {
            const drafted = await client.extractChecklist();
            onChange(drafted);
            toast.success(`Drafted ${drafted.checklist.length} checklist items. Review them before running QC.`);
            track('video_qc_checklist_drafted', { items: drafted.checklist.length });
        } catch (err) {
            toast.error(errorText(err));
        } finally {
            setBusy(null);
        }
    };

    const criticalCount = checklist.filter((i) => i.critical).length;
    const subtitle = checklist.length
        ? `The AI reviewer checks every video against these ${checklist.length} requirements. ${criticalCount} critical: missing one fails the video.`
        : 'Add the requirements the AI reviewer should check.';

    return (
        <div style={{ ...SURFACE, padding: '18px 20px' }}>
            <div style={{ marginBottom: 16 }}>
                <CardHeader icon={<FactCheckOutlinedIcon style={{ fontSize: 21 }} />} title="Brief & QC checklist"
                    subtitle={subtitle}
                    actions={!editing && (
                    <>
                        <Button variant="ai" disabled={!brief.trim() || busy !== null}
                            title={brief.trim() ? 'Draft the checklist from the brief with AI' : 'Add a brief first'}
                            onClick={() => (checklist.length ? setConfirmExtract(true) : extract())}>
                            <AutoAwesomeIcon aria-hidden="true" className={busy === 'extract' ? 'vqc-twinkle' : undefined} style={{ fontSize: 16, color: C.gold }} />
                            {busy === 'extract' ? 'Drafting…' : 'Draft checklist with AI'}
                        </Button>
                        <Button onClick={startEdit} disabled={busy !== null}><EditOutlinedIcon aria-hidden="true" style={{ fontSize: 15 }} />Edit</Button>
                    </>
                    )} />
            </div>

            {busy === 'extract' && <Drafting />}

            {confirmExtract && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#92400e' }}>
                    <span>This replaces the current {checklist.length} items with a fresh draft from the brief.</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button small onClick={() => setConfirmExtract(false)}>Cancel</Button>
                        <Button small variant="primary" onClick={extract}>Replace</Button>
                    </div>
                </div>
            )}

            {editing ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: C.body }}>
                        Brief{!brief && campaignBrief && <span style={{ fontWeight: 400, color: C.muted }}> (pre-filled from the campaign brief)</span>}
                        <textarea aria-label="Brief" value={draftBrief} onChange={(e) => setDraftBrief(e.target.value)} rows={6}
                            placeholder="Paste the campaign brief: mandatory messages, disclosures, visuals, format, do's and don'ts"
                            style={{ ...inputStyle, marginTop: 6, resize: 'vertical' }} />
                    </label>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.body }}>Checklist</div>
                    {draftItems.map((item, i) => (
                        <ItemEditor key={item.id} item={item} onChange={(next) => setItem(i, next)}
                            onRemove={() => setDraftItems(draftItems.filter((_, j) => j !== i))} />
                    ))}
                    <div><Button small onClick={addItem}>+ Add item</Button></div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        <Button onClick={() => setEditing(false)} disabled={busy !== null}>Cancel</Button>
                        <Button variant="primary" onClick={save} disabled={busy !== null}>{busy === 'save' ? 'Saving…' : 'Save'}</Button>
                    </div>
                </div>
            ) : (
                <>
                    {brief.trim() && (
                        <div style={{ marginBottom: 14 }}>
                            <Disclosure open={showBrief} controls={BRIEF_ID} onToggle={() => setShowBrief((v) => !v)}>
                                <DescriptionOutlinedIcon aria-hidden="true" style={{ fontSize: 16, color: C.muted }} />
                                {showBrief ? 'Hide brief' : 'Show brief'}
                            </Disclosure>
                            <p id={BRIEF_ID} hidden={!showBrief} style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.55, color: C.body, background: C.soft, border: `1px solid ${C.line}`, borderRadius: 10, padding: '12px 14px', margin: '10px 0 0' }}>{brief}</p>
                        </div>
                    )}
                    <ChecklistView checklist={checklist} />
                </>
            )}
        </div>
    );
}
